import { create } from "zustand";
import { persist } from "zustand/middleware";
import { DEFAULT_VAT_RATE, type BillingCycleMonths, type BillingCycleOption } from "@/types/resource";
import { getDefaultRegion } from "@/constants/regions";
import { roundToCurrency } from "@/lib/currency";

const STORAGE_PLANS = [
  { id: "starter", label: "Starter", quotaGb: 10 },
  { id: "standard", label: "Standard", quotaGb: 50 },
  { id: "professional", label: "Professional", quotaGb: 100 },
  { id: "enterprise", label: "Enterprise", quotaGb: 500 },
  { id: "custom", label: "Custom", quotaGb: 0 },
] as const;

// No per-GB rate, discount schedule or VAT rate is declared here. All three are configuration the
// backend owns (object-storage-gb ComponentPrice, billing-cycle-discount PricingRules, the VatRate
// PlatformSetting) and they reach this store through setEstimatedPricing from the
// /pricing/calculate quote — the same quote the order is charged from. This store used to compute
// all three locally, so an admin price change never reached the customer and the wizard advertised
// a rate the order did not apply (the WICL-430 defect, on the storage wizard).

export type StoragePlanId = (typeof STORAGE_PLANS)[number]["id"];

export { STORAGE_PLANS };

export interface StorageWizardState {
  currentStep: number;

  // Step 0: Plan selection
  selectedPlanId: StoragePlanId | null;
  customQuotaGb: number;

  // Step 1: Name & settings
  storageName: string;
  region: string;

  // Pricing (computed from plan quota x pricing rate)
  estimatedPricing: {
    hourly: number;
    monthly: number;
    currency: string;
    vatRate: number;
    billingCycleOptions?: BillingCycleOption[];
  } | null;

  // Payment fields (Steps 2-3)
  paymentCycle: BillingCycleMonths;
  orderId: string | null;
  orderNumber: string | null;
  orderTotal: number | null;
  paymentId: string | null;
  paymentOrderNumber: string | null;
  paymentStatus:
    | "Pending"
    | "AwaitingVerification"
    | "Completed"
    | "Failed"
    | "Cancelled"
    | null;
  paymentMethod: "QRCode" | "BankTransfer" | "AccountBalance" | null;
  paymentDetails: {
    qrCodeUrl?: string;
    bankAccountNumber?: string;
    bankAccountName?: string;
    bankName?: string;
    amount?: number;
    // T-PayGate: the VietQR bill is rendered inline from these — the image when
    // the gateway returns one, the raw content as the fallback.
    tPayGateBillCode?: string;
    tPayGateQrContent?: string;
    tPayGateQrImageBase64?: string;
    tPayGateExpiredAt?: string;
  } | null;
  isWizardCompleted: boolean;
  orderCreationCompleted: boolean;

  // Actions
  setCurrentStep: (step: number) => void;
  nextStep: () => void;
  previousStep: () => void;
  setSelectedPlan: (planId: StoragePlanId) => void;
  setCustomQuotaGb: (gb: number) => void;
  setStorageName: (name: string) => void;
  setRegion: (region: string) => void;
  setEstimatedPricing: (pricing: {
    hourly: number;
    monthly: number;
    currency: string;
    vatRate?: number;
    billingCycleOptions?: BillingCycleOption[];
  }) => void;
  setPaymentCycle: (cycle: BillingCycleMonths) => void;
  setOrderDetails: (
    orderId: string,
    orderNumber: string,
    total: number,
  ) => void;
  setPaymentDetails: (
    /**
     * Both are null when the order was settled from the account balance: that path creates no
     * Payment record, so neither a payment id nor a payment order number exists. The state
     * fields have always allowed null; only this setter did not, which is what pushed a made-up
     * id and a `WIC-BAL-<timestamp>` number into them.
     */
    paymentId: string | null,
    orderNumber: string | null,
    method: "QRCode" | "BankTransfer" | "AccountBalance",
    details: StorageWizardState["paymentDetails"],
  ) => void;
  updatePaymentStatus: (
    status: StorageWizardState["paymentStatus"],
  ) => void;
  getTotalAmount: () => {
    subtotal: number;
    tax: number;
    total: number;
  } | null;
  getQuotaGb: () => number;
  clearEstimatedPricing: () => void;
  resetWizard: () => void;
  clearWizardFormData: () => void;
  markWizardCompleted: () => void;
  markOrderCreationCompleted: () => void;
  getFormData: () => {
    name: string;
    quotaGb: number;
    region: string;
    currency: string;
  } | null;
}

const initialState = {
  currentStep: 0,
  selectedPlanId: null as StoragePlanId | null,
  customQuotaGb: 50,
  storageName: "",
  region: getDefaultRegion(),
  estimatedPricing: null,
  paymentCycle: 1 as const,
  orderId: null,
  orderNumber: null,
  orderTotal: null,
  paymentId: null,
  paymentOrderNumber: null,
  paymentStatus: null,
  paymentMethod: null,
  paymentDetails: null,
  isWizardCompleted: false,
  orderCreationCompleted: false,
};

export const useStorageWizardStore = create<StorageWizardState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setCurrentStep: (step) => set({ currentStep: step }),
      nextStep: () =>
        set((state) => ({ currentStep: Math.min(state.currentStep + 1, 3) })),
      previousStep: () =>
        set((state) => ({
          currentStep: Math.max(state.currentStep - 1, 0),
        })),
      setSelectedPlan: (planId) => set({ selectedPlanId: planId }),
      setCustomQuotaGb: (gb) => set({ customQuotaGb: gb }),
      setStorageName: (name) => set({ storageName: name }),
      setRegion: (region) => set({ region }),

      setEstimatedPricing: (pricing) =>
        set({
          estimatedPricing: {
            ...pricing,
            vatRate: pricing.vatRate ?? DEFAULT_VAT_RATE,
            billingCycleOptions: pricing.billingCycleOptions ?? undefined,
          },
        }),

      setPaymentCycle: (cycle) => set({ paymentCycle: cycle }),

      setOrderDetails: (orderId, orderNumber, total) =>
        set({
          orderId,
          orderNumber,
          orderTotal: total,
          orderCreationCompleted: true,
        }),

      markOrderCreationCompleted: () => set({ orderCreationCompleted: true }),

      setPaymentDetails: (paymentId, orderNumber, method, details) =>
        set({
          paymentId,
          paymentOrderNumber: orderNumber,
          paymentMethod: method,
          paymentDetails: details,
        }),

      updatePaymentStatus: (status) => set({ paymentStatus: status }),

      getTotalAmount: () => {
        const state = get();
        if (
          state.estimatedPricing?.monthly === undefined ||
          state.estimatedPricing?.monthly === null
        )
          return null;

        const monthlyPrice = state.estimatedPricing.monthly;
        const cycle = state.paymentCycle;
        const vatRate = state.estimatedPricing.vatRate ?? DEFAULT_VAT_RATE;

        const cycleOption = state.estimatedPricing.billingCycleOptions?.find(
          (option) => option.months === cycle,
        );

        // estimatedPricing is persisted to localStorage, so a state rehydrated from an older build
        // can arrive without a currency despite the type. Default to VND — the platform's base
        // currency and the only one whose rounding this code originally assumed.
        const currency = state.estimatedPricing.currency ?? "VND";

        let subtotal: number;
        if (cycleOption) {
          subtotal = cycleOption.totalPrice;
        } else {
          subtotal = roundToCurrency(monthlyPrice * cycle, currency);
        }

        const tax = roundToCurrency((subtotal * vatRate) / 100, currency);
        const total = subtotal + tax;

        return { subtotal, tax, total };
      },

      getQuotaGb: () => {
        const state = get();
        const plan = STORAGE_PLANS.find((p) => p.id === state.selectedPlanId);
        return state.selectedPlanId === "custom"
          ? state.customQuotaGb
          : plan?.quotaGb ?? 0;
      },

      clearEstimatedPricing: () => set({ estimatedPricing: null }),

      resetWizard: () => set(initialState),

      clearWizardFormData: () =>
        set({
          selectedPlanId: null,
          customQuotaGb: 50,
          storageName: "",
          region: getDefaultRegion(),
          estimatedPricing: null,
          paymentCycle: 1 as const,
        }),

      markWizardCompleted: () => set({ isWizardCompleted: true }),

      getFormData: () => {
        const state = get();
        const quotaGb = state.getQuotaGb();

        if (!state.storageName || !state.region || quotaGb <= 0) {
          return null;
        }

        return {
          name: state.storageName,
          quotaGb,
          region: state.region,
          currency: state.estimatedPricing?.currency || "VND",
        };
      },
    }),
    {
      name: "wicloud-storage-wizard",
      version: 3,
      migrate: (persistedState: unknown, version: number) => {
        const state = persistedState as Record<string, unknown>;
        if (version < 2) {
          state.estimatedPricing = null;
          state.paymentOrderNumber = null;
          state.paymentDetails = null;
          state.orderCreationCompleted = false;
        }
        // v3: estimatedPricing used to be computed client-side from a hardcoded per-GB rate,
        // discount schedule and VAT rate. A draft persisted by an older build would rehydrate
        // those invented numbers and, if the pricing query then failed, keep showing them —
        // the exact advertised-vs-charged divergence this store was changed to remove. Drop it
        // and let useObjectStoragePricing re-quote from the backend.
        if (version < 3) {
          state.estimatedPricing = null;
        }
        return state;
      },
      partialize: (state) => ({
        currentStep: state.currentStep,
        selectedPlanId: state.selectedPlanId,
        customQuotaGb: state.customQuotaGb,
        storageName: state.storageName,
        region: state.region,
        estimatedPricing: state.estimatedPricing,
        paymentCycle: state.paymentCycle,
        isWizardCompleted: state.isWizardCompleted,
        orderCreationCompleted: state.orderCreationCompleted,
      }),
    },
  ),
);
